import { unwrap, type SetupDraftOut, type WorkspaceOut } from "@sagot/api-client";
import { canonicalIndustry } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, ErrorState, Field, Icon, Input, Skeleton, Textarea, toast } from "@sagot/ui";
import { useState } from "react";
import { FormAlert } from "../auth/components/form-alert";
import { useErrorMessage } from "../lib/api-errors";
import { useApi } from "../session/session-provider";
import { DraftFields } from "./draft-fields";
import { EditorSkeleton, NameSkeleton } from "./draft-skeleton";
import { rankTemplates, switcherOptions } from "./ranking";
import { applyBody, configFromDraft, configProblems, configSignature, type SetupConfig } from "./setup-config";
import { TemplateSwitcher, type ReplaceKind } from "./template-switcher";
import { useTemplates } from "./use-catalog";
import { readOnboarding, useSaveProgress } from "./use-onboarding";
import { useRegenerateDraft, useSetupDraft } from "./use-setup-draft";
import { useTemplateSwitch } from "./use-template-switch";

interface StepConfigureProps {
  workspace: WorkspaceOut | undefined;
  onBack: () => void;
  onNext: () => void;
}

function DraftNotice({ draft }: { draft: SetupDraftOut }) {
  const { t } = useTranslation();
  const templates = useTemplates();
  const name = templates.data?.find((tpl) => tpl.key === draft.template_key)?.name ?? draft.template_key;
  return (
    <FormAlert tone="info" title={draft.source === "ai" ? t("onboarding.configure.sourceAi") : t("onboarding.configure.sourceTemplate", { template: name })}>
      {draft.fallback_reason ? t(`onboarding.configure.fallback_${draft.fallback_reason}`) : null}
    </FormAlert>
  );
}

function Done({ name, onBack, onNext }: { name: string; onBack: () => void; onNext: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div className="card card-body flex items-start gap-3" data-testid="setup-done">
        <Icon name="check-circle" className="mt-0.5 size-5 text-success" />
        <div>
          <h1 className="text-xl font-extrabold tracking-tight">{t("onboarding.configure.doneTitle")}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t("onboarding.configure.doneBody", { name })}</p>
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-line pt-5">
        <Button variant="ghost" onClick={onBack}>
          <Icon name="arrow-left" />
          {t("onboarding.back")}
        </Button>
        <Button onClick={onNext}>
          {t("onboarding.configure.next")}
          <Icon name="arrow-right" />
        </Button>
      </div>
    </div>
  );
}

interface DraftEditorProps {
  workspace: WorkspaceOut | undefined;
  draft: SetupDraftOut;
  onDraft: (draft: SetupDraftOut) => void;
  onBack: () => void;
  onNext: () => void;
}

/** The editable setup; remounted (keyed) whenever a new draft arrives. */
function DraftEditor({ workspace, draft, onDraft, onBack, onNext }: DraftEditorProps) {
  const { t } = useTranslation();
  const api = useApi();
  const save = useSaveProgress(workspace);
  const message = useErrorMessage();
  const regenerate = useRegenerateDraft(workspace);
  const templates = useTemplates();
  const [baseline] = useState(() => configFromDraft(draft));
  const [config, setConfig] = useState<SetupConfig>(baseline);
  const [description, setDescription] = useState(readOnboarding(workspace).data.description ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const switcher = useTemplateSwitch(workspace, onDraft, setError);
  const switching = switcher.target !== null;
  const set = (patch: Partial<SetupConfig>) => { setConfig((current) => ({ ...current, ...patch })); };
  const problems = configProblems(config);
  const industry = canonicalIndustry(workspace?.industry);
  const goals = workspace?.goals ?? [];
  const options = switcherOptions(templates.data ?? [], industry, goals, config.template_key);
  const edited = configSignature(config) !== configSignature(baseline);
  const replace: ReplaceKind = edited ? "edits" : draft.source === "ai" ? "ai" : null;
  const loadingName = options.find((option) => option.key === switcher.target)?.name ?? "";
  const runAi = async () => {
    try {
      onDraft(await regenerate.mutateAsync(description));
    } catch (caught) {
      setError(message(caught));
    }
  };
  const confirm = async () => {
    if (problems.length) return;
    setSaving(true);
    try {
      const created = await unwrap(api.POST("/v1/workspace/setup/apply", { body: applyBody(config) }));
      await save(4, { agent_id: created.agent_id, agent_name: created.agent_name, generate: false, description: description.trim() });
      toast.success(t("onboarding.configure.created"));
      onNext();
    } catch (caught) {
      setError(message(caught));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">{t("onboarding.configure.heading")}</h1>
        <p className="mt-1 text-fg-muted">{t("onboarding.configure.subtitle")}</p>
      </div>
      <DraftNotice draft={draft} />
      {error ? <FormAlert title={error} onDismiss={() => { setError(null); }} /> : null}
      <p className="sr-only" role="status" aria-live="polite">
        {switching ? t("onboarding.configure.switching", { template: loadingName }) : ""}
      </p>
      <section className="card card-body space-y-3" aria-label={t("onboarding.configure.describe")}>
        <Field label={t("onboarding.configure.describe")} hint={t("onboarding.org.describeHint")}>
          <Textarea rows={3} maxLength={1_000} value={description} onChange={(e) => { setDescription(e.target.value); }} />
        </Field>
        <Button variant="secondary" size="sm" loading={regenerate.isPending} disabled={switching} onClick={() => void runAi()}>
          <Icon name="sparkles" />
          {t("onboarding.configure.regenerate")}
        </Button>
      </section>
      <div className="card">
        <div className="card-body space-y-6" aria-busy={switching}>
          <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
            {switching ? (
              <NameSkeleton />
            ) : (
              <Field label={t("onboarding.configure.name")}>
                <Input value={config.assistant_name} maxLength={80} onChange={(e) => { set({ assistant_name: e.target.value }); }} />
              </Field>
            )}
            <TemplateSwitcher
              options={options}
              recommended={rankTemplates(templates.data ?? [], industry, goals)[0]?.key}
              current={config.template_key}
              loading={switcher.target}
              replace={replace}
              onSwitch={(key, name) => void switcher.run(key, name)}
            />
          </div>
          {switching ? <EditorSkeleton /> : <DraftFields config={config} onChange={set} />}
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-line pt-5">
        <Button variant="ghost" onClick={onBack}>
          <Icon name="arrow-left" />
          {t("onboarding.back")}
        </Button>
        <Button loading={saving} disabled={problems.length > 0 || switching} onClick={() => void confirm()}>
          {t("onboarding.configure.confirm")}
          <Icon name="arrow-right" />
        </Button>
      </div>
    </div>
  );
}

/** Step 3: review + edit the suggested setup (template or AI draft), then create the assistant. */
export function StepConfigure({ workspace, onBack, onNext }: StepConfigureProps) {
  const { t } = useTranslation();
  const saved = readOnboarding(workspace).data;
  const draftQuery = useSetupDraft(workspace, !saved.agent_id);
  const [override, setOverride] = useState<{ draft: SetupDraftOut; version: number } | null>(null);
  if (saved.agent_id) return <Done name={saved.agent_name ?? ""} onBack={onBack} onNext={onNext} />;
  if (draftQuery.isError) return <ErrorState title={t("onboarding.configure.draftError")} retryLabel={t("onboarding.configure.retry")} onRetry={() => void draftQuery.refetch()} />;
  const draft = override?.draft ?? draftQuery.data;
  if (!draft) {
    return (
      <div className="space-y-4" aria-busy="true" aria-label={t("onboarding.configure.loading")}>
        <Skeleton className="h-8 w-[320px] max-w-full" />
        <Skeleton className="h-72" />
      </div>
    );
  }
  const onDraft = (next: SetupDraftOut) => { setOverride({ draft: next, version: (override?.version ?? 0) + 1 }); };
  return <DraftEditor key={override?.version ?? 0} workspace={workspace} draft={draft} onDraft={onDraft} onBack={onBack} onNext={onNext} />;
}
